import {
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { phosphorByName } from "../icons/phosphor-by-name";
import {
  clamp,
  makeSim,
  nodeRadius,
  type ResolvedConfig,
  type Sim,
  seedPositions,
} from "./force-sim";
import type { GraphApi, GraphBeam, GraphConfig, GraphEdge, GraphNode } from "./types";

interface EdgeElRef {
  el: SVGPathElement;
  sid: string;
  tid: string;
  kind?: string;
}

export interface ForceGraphProps {
  nodes: GraphNode[];
  edges: GraphEdge[];
  config: GraphConfig;
  selectedId?: string | null;
  hoveredId?: string | null;
  matchIds?: Set<string> | null;
  beam?: GraphBeam | null;
  onSelect?: (id: string | null) => void;
  onHover?: (id: string | null) => void;
  onReady?: (api: GraphApi) => void;
  /** Member photo rendered inside the core node's disc (a resolved object URL). */
  coreImageUrl?: string | null;
}

/**
 * The shared force-directed graph. A velocity-Verlet simulation owns node
 * positions in refs and writes transforms/edge geometry to the DOM in one rAF
 * loop; React renders structure and highlight classes but never coordinates, so
 * re-renders never fight the sim. The loop pauses while the tab is hidden.
 */

/*
 * SVG text does not wrap, so a label longer than a couple of words rendered as
 * one long line straight through its neighbours. Two lines, then an ellipsis
 * with the full text on the node's title, which is the Carbon rule: never
 * truncate without somewhere to read the whole string, and never truncate to
 * fewer than four characters.
 */
const LABEL_LINE_CHARS = 18;
const LABEL_MAX_LINES = 2;

export function wrapLabel(label: string): string[] {
  const words = label.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [""];
  const lines: string[] = [];
  let line = "";
  for (const word of words) {
    const next = line ? `${line} ${word}` : word;
    if (next.length <= LABEL_LINE_CHARS || !line) {
      line = next;
      continue;
    }
    lines.push(line);
    line = word;
    if (lines.length === LABEL_MAX_LINES) break;
  }
  if (lines.length < LABEL_MAX_LINES && line) lines.push(line);
  const last = lines[lines.length - 1];
  const overflowed = lines.join(" ").length < label.trim().length;
  if (overflowed && last !== undefined) {
    lines[lines.length - 1] =
      last.length > LABEL_LINE_CHARS - 1
        ? `${last.slice(0, LABEL_LINE_CHARS - 1)}\u2026`
        : `${last}\u2026`;
  }
  return lines;
}

export function ForceGraph({
  nodes,
  edges,
  config,
  selectedId,
  hoveredId,
  matchIds,
  beam,
  onSelect,
  onHover,
  onReady,
  coreImageUrl,
}: ForceGraphProps) {
  const [dims, setDims] = useState<{ w: number; h: number } | null>(null);

  const cfg = useMemo<ResolvedConfig>(() => {
    const base: ResolvedConfig = {
      width: 1000,
      height: 720,
      charge: -1600,
      linkDistance: 92,
      collide: 8,
      layout: "clustered",
      designWidth: 1000,
      xStretch: 1,
      ...config,
    };
    base.designWidth = base.width;
    if (dims && dims.w > 0 && dims.h > 0) {
      const aspect = dims.w / dims.h;
      base.width = Math.max(base.width, Math.round(base.height * aspect));
      if (base.fitInsetLeft) {
        const unit = dims.h / base.height;
        const panelPx = base.fitInsetLeftPx || 316;
        base.fitInsetLeft = Math.max(base.fitInsetLeft, Math.round(panelPx / unit));
      }
      const usable = base.width - (base.fitInsetLeft || 0);
      base.xStretch = clamp(usable / base.designWidth, 1, 1.55);
    } else {
      base.xStretch = 1;
    }
    return base;
  }, [config, dims]);

  const wrapRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const vpRef = useRef<SVGGElement>(null);
  const nodeEls = useRef<Map<string, SVGGElement>>(new Map());
  const edgeEls = useRef<(EdgeElRef | undefined)[]>([]);
  const simRef = useRef<Sim | null>(null);
  const rafRef = useRef(0);
  /** Restarts the settle loop; the loop stops itself once the layout is still. */
  const restartRef = useRef<() => void>(() => {});
  const viewRef = useRef({ x: 0, y: 0, k: 1 });
  const dragRef = useRef<{
    id: string;
    idx: number;
    dx: number;
    dy: number;
    moved: boolean;
    sx: number;
    sy: number;
  } | null>(null);
  const panRef = useRef<{ x: number; y: number; vx: number; vy: number } | null>(null);
  const uid = useId().replace(/:/g, "");
  const userAdjustedRef = useRef(false);
  const adjRef = useRef<Map<string, Set<string>>>(new Map());
  // Latest onReady without making it a sim dependency: an inline callback from
  // the parent must not rebuild the sim (and reset the view) on every render.
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  useEffect(() => {
    const adj = new Map<string, Set<string>>();
    nodes.forEach((n) => {
      adj.set(n.id, new Set());
    });
    edges.forEach((e) => {
      adj.get(e.source)?.add(e.target);
      adj.get(e.target)?.add(e.source);
    });
    adjRef.current = adj;
  }, [nodes, edges]);

  // measure the stage, feed pixel size into the responsive coord space
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    let raf = 0;
    const measure = () => {
      const r = el.getBoundingClientRect();
      if (r.width < 40 || r.height < 40) return;
      setDims((d) =>
        d && Math.abs(d.w - r.width) < 24 && Math.abs(d.h - r.height) < 24
          ? d
          : { w: Math.round(r.width), h: Math.round(r.height) },
      );
    };
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    });
    ro.observe(el);
    measure();
    return () => {
      ro.disconnect();
      cancelAnimationFrame(raf);
    };
  }, []);

  const animateView = useCallback((to: { x: number; y: number; k: number }) => {
    const from = { ...viewRef.current };
    const t0 = performance.now();
    const dur = 520;
    const ease = (t: number) => 1 - (1 - t) ** 3;
    const stepv = (now: number) => {
      const t = clamp((now - t0) / dur, 0, 1);
      const e = ease(t);
      viewRef.current = {
        x: from.x + (to.x - from.x) * e,
        y: from.y + (to.y - from.y) * e,
        k: from.k + (to.k - from.k) * e,
      };
      const { x, y, k } = viewRef.current;
      vpRef.current?.setAttribute("transform", `translate(${x} ${y}) scale(${k})`);
      if (t < 1) requestAnimationFrame(stepv);
    };
    requestAnimationFrame(stepv);
  }, []);

  // set up the simulation once per data/config
  // biome-ignore lint/correctness/useExhaustiveDependencies: sim rebuilds on data/config only; animateView is stable
  useEffect(() => {
    seedPositions(nodes, cfg, edges);
    const sim = makeSim(nodes, edges, cfg);
    simRef.current = sim;

    const applyView = () => {
      const { x, y, k } = viewRef.current;
      vpRef.current?.setAttribute("transform", `translate(${x} ${y}) scale(${k})`);
    };

    // position of a node by id; every rendered node has a particle, so the
    // lookup is total for valid ids.
    const pos = (id: string) => sim.P[sim.idIndex.get(id) ?? 0] as (typeof sim.P)[number];

    const computeFit = (pad = 56, ids: string[] | null = null, maxK = 1.6) => {
      const idSet = ids?.length ? new Set(ids) : null;
      const src = idSet ? nodes.filter((n) => idSet.has(n.id)) : nodes;
      const xs = src.map((n) => pos(n.id).x);
      const ys = src.map((n) => pos(n.id).y);
      let minX = Math.min(...xs);
      let maxX = Math.max(...xs);
      let minY = Math.min(...ys);
      let maxY = Math.max(...ys);
      minX -= 50;
      maxX += 50;
      minY -= 48;
      maxY += 60;
      const bw = maxX - minX;
      const bh = maxY - minY;
      const insetL = cfg.fitInsetLeft || 0;
      const k = clamp(
        Math.min((cfg.width - pad * 2 - insetL) / bw, (cfg.height - pad * 2) / bh),
        0.5,
        maxK,
      );
      const bcx = (minX + maxX) / 2;
      const bcy = (minY + maxY) / 2;
      return { x: cfg.width / 2 + insetL / 2 - bcx * k, y: cfg.height / 2 - bcy * k, k };
    };
    const fitView = (force?: boolean) => {
      if (force || !userAdjustedRef.current) animateView(computeFit());
    };

    // A member-adjusted view survives sim rebuilds (data refetch); only an
    // untouched view re-fits to the new shape.
    if (!userAdjustedRef.current) viewRef.current = computeFit();
    applyView();

    const ecx = cfg.width / 2;
    const ecy = cfg.height / 2;
    const draw = () => {
      for (const n of nodes) {
        const el = nodeEls.current.get(n.id);
        if (!el) continue;
        const p = sim.P[sim.idIndex.get(n.id) ?? 0];
        if (p) el.setAttribute("transform", `translate(${p.x.toFixed(2)} ${p.y.toFixed(2)})`);
      }
      for (const e of edgeEls.current) {
        if (!e) continue;
        const si = sim.idIndex.get(e.sid);
        const ti = sim.idIndex.get(e.tid);
        if (si == null || ti == null) continue;
        const a = sim.P[si];
        const b = sim.P[ti];
        if (!a || !b) continue;
        const mx = (a.x + b.x) / 2;
        const my = (a.y + b.y) / 2;
        const L = Math.hypot(b.x - a.x, b.y - a.y);
        let rx = mx - ecx;
        let ry = my - ecy;
        const rl = Math.hypot(rx, ry) || 1;
        rx /= rl;
        ry /= rl;
        const bow = Math.min(L * 0.16, 74);
        const cxp = mx + rx * bow;
        const cyp = my + ry * bow;
        e.el.setAttribute(
          "d",
          `M${a.x.toFixed(1)} ${a.y.toFixed(1)} Q ${cxp.toFixed(1)} ${cyp.toFixed(1)} ${b.x.toFixed(1)} ${b.y.toFixed(1)}`,
        );
      }
    };
    draw();

    let hidden = typeof document !== "undefined" && document.visibilityState === "hidden";
    /*
     * The loop stops when the layout settles, and this is the whole of the
     * jitter fix.
     *
     * The spring and charge passes are scaled by alpha, so they fade to nothing
     * at rest, but the disc and label separation passes are not: two nodes
     * whose labels overlap keep shoving each other apart on every frame with no
     * spring left to pull them back. Running the loop forever meant the map was
     * never actually still, and any hover that changed a measurement fed
     * straight back into it. A shallow tree does not need a live simulation; it
     * needs a position, once.
     */
    const tick = () => {
      const alpha = sim.step();
      draw();
      if (alpha <= 0) {
        rafRef.current = 0;
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    if (!hidden) rafRef.current = requestAnimationFrame(tick);
    // pause the loop while the tab is hidden; resume (and reheat) on return
    const onVisibility = () => {
      const nowHidden = document.visibilityState === "hidden";
      if (nowHidden === hidden) return;
      hidden = nowHidden;
      if (hidden) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
      } else {
        // Nothing to recompute: the layout is where it was left. Draw once so
        // the canvas is correct after a resize behind a hidden tab.
        draw();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    const fitTimers = [setTimeout(() => fitView(), 350)];

    const run = () => {
      if (rafRef.current || hidden) return;
      rafRef.current = requestAnimationFrame(tick);
    };
    restartRef.current = run;

    onReadyRef.current?.({
      reheat: (a) => {
        sim.reheat(a);
        run();
      },
      resetView: () => {
        userAdjustedRef.current = false;
        fitView(true);
        sim.reheat(0.4);
        run();
      },
      center: () => fitView(true),
      fitView,
      flyTo: (id) => {
        const idx = sim.idIndex.get(id);
        if (idx == null) return;
        const p = sim.P[idx];
        if (!p) return;
        const k = 1.3;
        animateView({ x: cfg.width / 2 - p.x * k, y: cfg.height / 2 - p.y * k, k });
      },
      fitToNodes: (ids) => animateView(computeFit(120, ids, 1.15)),
    });

    return () => {
      cancelAnimationFrame(rafRef.current);
      document.removeEventListener("visibilitychange", onVisibility);
      fitTimers.forEach(clearTimeout);
    };
  }, [nodes, edges, cfg]);

  const toGraph = useCallback(
    (clientX: number, clientY: number) => {
      const svg = svgRef.current;
      if (!svg) return { x: 0, y: 0 };
      const rect = svg.getBoundingClientRect();
      const sx = ((clientX - rect.left) / rect.width) * cfg.width;
      const sy = ((clientY - rect.top) / rect.height) * cfg.height;
      const { x, y, k } = viewRef.current;
      return { x: (sx - x) / k, y: (sy - y) / k };
    },
    [cfg],
  );

  const onNodePointerDown = useCallback(
    (e: ReactPointerEvent<SVGGElement>, id: string) => {
      e.stopPropagation();
      userAdjustedRef.current = true;
      const sim = simRef.current;
      if (!sim) return;
      const idx = sim.idIndex.get(id);
      if (idx == null) return;
      const p = sim.P[idx];
      if (!p) return;
      const g = toGraph(e.clientX, e.clientY);
      dragRef.current = {
        id,
        idx,
        dx: p.x - g.x,
        dy: p.y - g.y,
        moved: false,
        sx: e.clientX,
        sy: e.clientY,
      };
      p.fx = p.x;
      p.fy = p.y;
      e.currentTarget.setPointerCapture?.(e.pointerId);
    },
    [toGraph],
  );

  const onPointerMove = useCallback(
    (e: ReactPointerEvent) => {
      const sim = simRef.current;
      if (!sim) return;
      if (dragRef.current) {
        const d = dragRef.current;
        if (!d.moved && Math.hypot(e.clientX - d.sx, e.clientY - d.sy) < 4) return;
        const g = toGraph(e.clientX, e.clientY);
        const p = sim.P[d.idx];
        if (p) {
          if (!d.moved) sim.reheat(0.5);
          restartRef.current();
          p.fx = g.x + d.dx;
          p.fy = g.y + d.dy;
          d.moved = true;
          sim.reheat(0.35);
          restartRef.current();
        }
      } else if (panRef.current) {
        const p = panRef.current;
        viewRef.current = {
          ...viewRef.current,
          x: p.vx + (e.clientX - p.x),
          y: p.vy + (e.clientY - p.y),
        };
        const { x, y, k } = viewRef.current;
        vpRef.current?.setAttribute("transform", `translate(${x} ${y}) scale(${k})`);
      }
    },
    [toGraph],
  );

  const onPointerUp = useCallback(() => {
    const sim = simRef.current;
    if (dragRef.current && sim) {
      const d = dragRef.current;
      const p = sim.P[d.idx];
      if (p) {
        p.fx = null;
        p.fy = null;
      }
      if (!d.moved) onSelect?.(d.id === selectedId ? null : d.id);
      dragRef.current = null;
    }
    panRef.current = null;
  }, [onSelect, selectedId]);

  const onBgPointerDown = useCallback((e: ReactPointerEvent) => {
    userAdjustedRef.current = true;
    panRef.current = {
      x: e.clientX,
      y: e.clientY,
      vx: viewRef.current.x,
      vy: viewRef.current.y,
    };
  }, []);

  const onBgClick = useCallback(
    (e: ReactMouseEvent) => {
      const t = e.target as Element;
      if (t === svgRef.current || t === vpRef.current || t.classList?.contains("fg-bg")) {
        onSelect?.(null);
      }
    },
    [onSelect],
  );

  // non-passive wheel zoom (around pointer)
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      userAdjustedRef.current = true;
      const { x, y, k } = viewRef.current;
      // scale by the actual wheel delta: trackpads fire many small-delta
      // events per gesture and a fixed per-event factor made them explosive
      const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? 120 : 1;
      const factor = clamp(Math.exp(-e.deltaY * unit * 0.0015), 0.85, 1.18);
      const nk = clamp(k * factor, 0.5, 2.6);
      if (nk === k) return;
      const rect = el.getBoundingClientRect();
      const px = ((e.clientX - rect.left) / rect.width) * cfg.width;
      const py = ((e.clientY - rect.top) / rect.height) * cfg.height;
      viewRef.current = { k: nk, x: px - (px - x) * (nk / k), y: py - (py - y) * (nk / k) };
      const v = viewRef.current;
      vpRef.current?.setAttribute("transform", `translate(${v.x} ${v.y}) scale(${v.k})`);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [cfg]);

  const focusId = hoveredId || selectedId || null;
  const neighbours = focusId ? adjRef.current.get(focusId) : null;
  const clusterColor = (c: string | undefined) =>
    (c && cfg.clusters?.[c]?.color) || "var(--color-teal-500)";
  // constant-time lookups for the per-node/per-edge render loops
  const nodeById = new Map(nodes.map((n) => [n.id, n]));
  const litNodes = beam?.nodes ? new Set(beam.nodes) : null;
  const litEdges = beam?.edges ? new Set(beam.edges.map((e) => `${e.source}|${e.target}`)) : null;

  return (
    <div
      className={`fg-wrap${focusId ? " is-focusing" : ""}${matchIds ? " is-searching" : ""}`}
      ref={wrapRef}
    >
      {/* The interactive affordances are the focusable node buttons below; the
          svg is a labelled graphics container. */}
      <svg
        ref={svgRef}
        className="fg-svg"
        viewBox={`0 0 ${cfg.width} ${cfg.height}`}
        aria-label={
          cfg.ariaLabel || "Interactive graph. Drag a node, scroll to zoom, drag the canvas to pan."
        }
        preserveAspectRatio="xMidYMid meet"
        onPointerDown={onBgPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
        onClick={onBgClick}
        onKeyDown={(e) => {
          if (e.key === "Escape") onSelect?.(null);
        }}
      >
        <rect
          className="fg-bg"
          x="0"
          y="0"
          width={cfg.width}
          height={cfg.height}
          fill="transparent"
        />
        <g ref={vpRef}>
          <g className="fg-edges">
            {edges.map((e, i) => {
              const dim = focusId && e.source !== focusId && e.target !== focusId;
              const lit =
                litEdges?.has(`${e.source}|${e.target}`) ||
                litEdges?.has(`${e.target}|${e.source}`);
              const cls = `fg-edge${e.kind ? ` k-${e.kind}` : ""}${dim ? " is-dim" : ""}${lit ? " is-lit" : ""}`;
              const stroke = clusterColor(nodeById.get(e.source)?.cluster);
              return (
                <path
                  // biome-ignore lint/suspicious/noArrayIndexKey: edges are positional and keyed to edgeEls by index
                  key={i}
                  ref={(el) => {
                    if (el) edgeEls.current[i] = { el, sid: e.source, tid: e.target, kind: e.kind };
                  }}
                  className={cls}
                  style={{ "--edge": stroke } as CSSProperties}
                  fill="none"
                />
              );
            })}
          </g>

          <g className="fg-nodes">
            {nodes.map((n) => {
              const isSel = selectedId === n.id;
              const isHov = hoveredId === n.id;
              const isNeighbour = neighbours?.has(n.id);
              const dim = focusId && !isSel && !isHov && !isNeighbour;
              const match = matchIds?.has(n.id);
              const lit = litNodes?.has(n.id);
              const r = nodeRadius(n);
              const cls =
                `fg-node k-${n.kind || "leaf"}` +
                `${isSel ? " is-selected" : ""}${isHov ? " is-hover" : ""}` +
                `${isNeighbour ? " is-neighbour" : ""}${dim ? " is-dim" : ""}` +
                `${match ? " is-match" : ""}${lit ? " is-lit" : ""}` +
                `${n.empty ? " is-empty" : ""}`;
              const Icon = n.icon ? phosphorByName(n.icon) : null;
              return (
                // biome-ignore lint/a11y/useSemanticElements: SVG group cannot be a button element
                <g
                  key={n.id}
                  className={cls}
                  style={{ "--accent": clusterColor(n.cluster) } as CSSProperties}
                  ref={(el) => {
                    if (el) nodeEls.current.set(n.id, el);
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={n.label + (n.sub ? `, ${n.sub}` : "")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onSelect?.(n.id);
                    }
                  }}
                  onPointerDown={(e) => onNodePointerDown(e, n.id)}
                  onPointerEnter={() => onHover?.(n.id)}
                  onPointerLeave={() => onHover?.(null)}
                >
                  <circle className="fg-node-halo" r={r + 10} />
                  <circle className="fg-node-disc" r={r} />
                  {n.kind === "core" && coreImageUrl && (
                    <>
                      <clipPath id={`${uid}-core-clip`}>
                        <circle r={r - 2} />
                      </clipPath>
                      <image
                        href={coreImageUrl}
                        x={-(r - 2)}
                        y={-(r - 2)}
                        width={(r - 2) * 2}
                        height={(r - 2) * 2}
                        clipPath={`url(#${uid}-core-clip)`}
                        preserveAspectRatio="xMidYMid slice"
                        style={{ pointerEvents: "none" }}
                      />
                    </>
                  )}
                  <circle className="fg-node-ring" r={r} />
                  {Icon && (
                    <foreignObject
                      x={-r}
                      y={n.mono ? -r : -12}
                      width={r * 2}
                      height={n.mono ? r : 24}
                      style={{ overflow: "visible", pointerEvents: "none" }}
                    >
                      <div className="fg-node-ic-wrap">
                        <Icon size={n.kind === "hub" ? 23 : 20} weight="regular" />
                      </div>
                    </foreignObject>
                  )}
                  {n.abbr && !(n.kind === "core" && coreImageUrl) && (
                    <text className="fg-node-abbr" y="1">
                      {n.abbr}
                    </text>
                  )}
                  {n.mono && (
                    <text className="fg-node-mono" y={r * 0.44}>
                      {n.mono}
                    </text>
                  )}
                  <text className="fg-node-label" y={r + 20}>
                    {wrapLabel(n.label).map((line, li) => (
                      <tspan key={line} x={0} dy={li === 0 ? 0 : 13}>
                        {line}
                      </tspan>
                    ))}
                    <title>{n.label}</title>
                  </text>
                  {n.sub && (
                    <text className="fg-node-sub" y={r + 21 + wrapLabel(n.label).length * 13}>
                      {n.sub}
                    </text>
                  )}
                </g>
              );
            })}
          </g>
        </g>
      </svg>
    </div>
  );
}
